import {
  canonical,
  description,
  join,
  meta,
  notFoundAction,
  openGraph,
  permanentRedirectAction,
  renderAction,
  title,
} from '@rea-argonaut/actions';
import { GetAction } from '@property.com.au/argonaut';
import { createUrqlClient, writeUrqlCacheToExchange } from '../../graphql/urqlClient';
import { fetchSuburbPage } from '../graphql/fetchSuburbPage';
import { getSlugFromPathname } from '../../helpers/getSlugFromPathname';
import { trackView } from '@rea-argonaut/analytics';
import { getCampaignData, getPageData } from '@realestate.com.au/iglu-data';
import { getPcaUserData } from '../../helpers/getPcaUserData';
import ogImageJpg from '../../assets/og-image.jpg';
import { Provider as UrqlProvider } from 'urql';
import { SuburbPageScreen } from '../screens/SuburbPageScreen';
import { UrlContextProvider } from '../../contexts/UrlContext/UrlContext';
import { SuburbPageModalContextProvider } from '../contexts/SuburbPageModalContext/SuburbPageModalContext';
import { getEntryRedirectUrl } from '../../helpers/getEntryRedirectUrl';

export const getAction: GetAction = async ({ url, user, optimizelyManager }) => {
  const redirectURL = getEntryRedirectUrl(url);
  if (redirectURL) {
    return permanentRedirectAction(redirectURL.href);
  }

  const urqlClient = await createUrqlClient();

  const toggleFlatmatesBrick = optimizelyManager.isFeatureEnabled('flatmates_brick_on_suburb_page');

  const { data, error } = await fetchSuburbPage({
    client: urqlClient,
    slug: getSlugFromPathname(url.pathname),
    toggleFlatmatesBrick,
  });

  if (error?.networkError) {
    throw Error('Could not successfully connect to Experience API');
  }

  if (!data?.suburbPageBySlug) {
    if (error && error.graphQLErrors.length > 0) {
      throw Error(
        'GraphQL error occurred and no suburb page data was received from Experience API',
      );
    }
    return notFoundAction();
  }

  await writeUrqlCacheToExchange();

  const {
    helmet: { title: pageTitle, description: pageDescription, canonical: pageCanonical, siteName },
  } = data.suburbPageBySlug;

  return renderAction(async () => {
    trackView([
      getPageData({
        pageType: 'suburb',
        siteSection: 'state_suburb_street',
        site: 'pca',
      }),
      getPcaUserData(user),
      getCampaignData(),
    ]);

    return {
      getElements: async () => {
        return {
          Screen: (
            <UrqlProvider value={urqlClient}>
              <UrlContextProvider url={url}>
                <SuburbPageModalContextProvider>
                  <SuburbPageScreen />
                </SuburbPageModalContextProvider>
              </UrlContextProvider>
            </UrqlProvider>
          ),
        };
      },
      getHead: async () =>
        join(
          title(pageTitle),
          description(pageDescription),
          canonical(pageCanonical),
          openGraph({
            property: 'og:locale',
            content: 'en_AU',
          }),
          openGraph({
            property: 'og:type',
            content: 'website',
          }),
          openGraph({
            property: 'og:title',
            content: pageTitle,
          }),
          openGraph({
            property: 'og:description',
            content: pageDescription,
          }),
          openGraph({
            property: 'og:url',
            content: pageCanonical,
          }),
          openGraph({
            property: 'og:site_name',
            content: siteName,
          }),
          openGraph({
            property: 'og:image',
            content: ogImageJpg,
          }),
          meta({
            name: 'robots',
            content: 'index, follow, noodp',
          }),
        ),
    };
  });
};
